import { create } from 'zustand';
import { message } from 'antd';
import i18n from '../lang/i18n';
import {
  ExtraData,
  normalizeExtraData,
  readCachedExtraData,
  writeCachedExtraData,
  updateExtraData,
} from '../api/UserSettings';

/** Cài đặt 1 bảng. Lưu cột ẨN (không lưu cột hiện) để cột mới thêm sau tự hiện. */
export interface TableSetting {
  v: number;
  order?: string[];
  hidden?: string[];
  /** 'none' = người dùng chủ động bỏ cố định cột vốn cố định mặc định */
  fixed?: Record<string, 'left' | 'right' | 'none'>;
  widths?: Record<string, number>;
}

export const TABLE_SETTING_VERSION = 1;

interface UserSettingsState {
  extraData: ExtraData;
  /** Nạp lại từ server (RefreshAuth / login). Bỏ qua khi đang có thay đổi chưa lưu. */
  hydrate: (raw: unknown) => void;
  getTableSetting: (tableKey: string) => TableSetting | null;
  /** null = xoá cài đặt của bảng (về mặc định) */
  setTableSetting: (tableKey: string, setting: TableSetting | null) => void;
}

const SAVE_DELAY = 700;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let dirty = false;

const useUserSettingsStore = create<UserSettingsState>((set, get) => {
  const flush = async () => {
    saveTimer = null;
    const res = await updateExtraData(get().extraData);
    // Có thay đổi mới trong lúc đang gửi -> để lần lưu sau đánh dấu sạch
    if (!saveTimer) dirty = false;
    if (!res.status) message.error(i18n.t('table_setting.save_failed'));
  };

  return {
    extraData: readCachedExtraData(),

    hydrate: (raw) => {
      if (dirty) return;
      const extraData = normalizeExtraData(raw);
      set({ extraData });
      writeCachedExtraData(extraData);
    },

    getTableSetting: (tableKey) => {
      const setting = get().extraData?.table_settings?.[tableKey];
      if (!setting || setting.v !== TABLE_SETTING_VERSION) return null;
      return setting as TableSetting;
    },

    setTableSetting: (tableKey, setting) => {
      const prev = get().extraData;
      const tableSettings = { ...(prev.table_settings || {}) };
      // Gán null thay vì delete để BE (dù replace hay merge) đều xoá được cài đặt cũ
      tableSettings[tableKey] = setting;

      const extraData = { ...prev, table_settings: tableSettings };
      set({ extraData });
      writeCachedExtraData(extraData);

      dirty = true;
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(flush, SAVE_DELAY);
    },
  };
});

export default useUserSettingsStore;
